import React, {ReactNode} from 'react'
import {split, path} from 'ramda'
import {css} from '@emotion/core'
import styled from '@emotion/styled'
import isPropValid from '@emotion/is-prop-valid'

import {AlignTextType} from 'types/Fonts'

import {TextAppearanceType} from 'types/Typography'

import {SpacingPropsType} from 'types/Layout'
import {ThemeType} from 'types/Theme'

// Extract all nested objects keys as a flattened string union type like `"piano.p100" | "piano.p200"` etc.
// https://stackoverflow.com/a/68404823
type DotPrefix<T extends string> = T extends "" ? "" : `.${T}`
type DotNestedKeys<T> = (T extends Record<string, unknown> ?
  { [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}` }[Exclude<keyof T, symbol>]
  : "") extends infer D ? Extract<D, string> : never;

type TextPropsType = {
  /** Text alignment */
  align?: AlignTextType
  /** HTML Element that text will be rendered */
  as?: string
  /** Text to be displayed */
  children: ReactNode | string
  /** HTML class to be include in the tag */
  className?: string
  /** Color's path to be used as text color (see in Colors) */
  colorPath?: DotNestedKeys<ThemeType['colors']>
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Margin bottom */
  marginBottomSize?: SpacingPropsType
  /** Margin top */
  marginTopSize?: SpacingPropsType
  /** The text appearance style from the theme */
  appearance?: TextAppearanceType
}

const TextElement = styled('p', {shouldForwardProp: isPropValid})<TextPropsType>(
  ({
    theme,
    colorPath,
    align,
    marginBottomSize,
    marginTopSize,
    appearance
  }) => css`
    ${colorPath && `color: ${path(split('.', colorPath), theme.colors)};`}
    text-align: ${align};
    margin-bottom: ${theme.layout.spacing[marginBottomSize]}px;
    margin-top: ${theme.layout.spacing[marginTopSize]}px;
    ${theme.typography.text[appearance]}
  `
)

const Text = ({as, ...props}: TextPropsType) => {
  const {
    align,
    children,
    className,
    colorPath,
    dataId,
    marginBottomSize,
    marginTopSize,
    appearance
  } = props

  return (
    <TextElement
      align={align}
      as={as}
      className={className}
      colorPath={colorPath}
      data-id={dataId}
      marginBottomSize={marginBottomSize}
      marginTopSize={marginTopSize}
      appearance={appearance}
    >
      {children}
    </TextElement>
  )
}

Text.defaultProps = {
  align: 'left',
  as: 'p',
  className: null,
  marginBottomSize: 's000',
  marginTopSize: 's000',
  appearance: 'regular'
}

export default Text
